import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, MapPin } from 'lucide-react';
import api from '../../lib/api';

/**
 * Dense, structural grid of producers. Cards now use a rotating slideshow
 * of the producer's event flyers as background (old + new).
 */
export default function FeaturedProducersGrid() {
  const [producers, setProducers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/public/producers?limit=12');
        const list = Array.isArray(res.data) ? res.data : [];
        list.sort((a, b) => {
          const v = (b.is_verified ? 1 : 0) - (a.is_verified ? 1 : 0);
          if (v !== 0) return v;
          return (b.upcoming_events_count || 0) - (a.upcoming_events_count || 0);
        });
        if (mounted) setProducers(list.slice(0, 8));
      } catch (e) {
        if (mounted) setProducers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="producers-loading">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="aspect-[4/5] rounded-xl bg-white/[0.03] animate-pulse" />
        ))}
      </div>
    );
  }
  if (producers.length === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="producers-grid">
      {producers.map((p, idx) => (
        <ProducerCard key={p.id} producer={p} delay={idx * 0.04} index={idx} />
      ))}
    </div>
  );
}

function ProducerCard({ producer, delay = 0, index = 0 }) {
  const accent = producer.color || '#D4AF37';
  const flyers = Array.isArray(producer.event_flyers) ? producer.event_flyers : [];
  const hasFlyers = flyers.length > 0;
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (flyers.length <= 1) return undefined;
    // Stagger initial offset per card so they don't all rotate in sync
    const startDelay = (index * 600) % 2500;
    const interval = setTimeout(() => {
      setActiveIdx(1 % flyers.length);
    }, startDelay);
    return () => clearTimeout(interval);
  }, [flyers.length, index]);

  useEffect(() => {
    if (flyers.length <= 1) return undefined;
    const t = setTimeout(() => {
      setActiveIdx((prev) => (prev + 1) % flyers.length);
    }, 4200);
    return () => clearTimeout(t);
  }, [activeIdx, flyers.length]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/productora/${producer.slug}`}
        className="group relative block rounded-xl border border-white/10 hover:border-white/30 hover:-translate-y-1 transition-all duration-300 overflow-hidden bg-[#0A0A0A]"
        data-testid={`producer-card-${producer.id}`}
      >
        {/* Background slideshow */}
        <div className="absolute inset-0 overflow-hidden" data-testid={`producer-card-bg-${producer.id}`}>
          <AnimatePresence mode="sync">
            {hasFlyers ? (
              <motion.div
                key={`${producer.id}-${activeIdx}`}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${flyers[activeIdx]})` }}
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, ${accent}40, transparent 60%)`,
                }}
              />
            )}
          </AnimatePresence>
          {/* Cinematic gradient — dark at bottom for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/20" />
          {/* Accent glow on hover */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ boxShadow: `inset 0 0 80px ${accent}30` }}
          />
        </div>

        <div className="relative z-10 aspect-[4/5] flex flex-col justify-between p-4">
          {/* Top: logo + verified */}
          <div className="flex items-center justify-between">
            <div
              className="relative w-12 h-12 rounded-lg overflow-hidden bg-black/40 border border-white/15 flex-shrink-0 backdrop-blur-sm"
              style={{ boxShadow: `0 0 0 1px ${accent}40` }}
            >
              {producer.logo ? (
                <img
                  src={producer.logo}
                  alt={producer.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-base font-black"
                  style={{ background: `${accent}35`, color: accent, fontFamily: "'Unbounded', sans-serif" }}
                >
                  {producer.name?.charAt(0) || '?'}
                </div>
              )}
            </div>
            {producer.is_verified && (
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm border border-emerald-400/40 text-emerald-300"
                title="Verificada"
              >
                <ShieldCheck size={13} />
              </span>
            )}
          </div>

          {/* Bottom: name + meta */}
          <div>
            <h4
              className="text-sm sm:text-base font-extrabold text-white leading-tight truncate group-hover:text-[#D4AF37] transition-colors"
              style={{ fontFamily: "'Unbounded', sans-serif", textShadow: '0 2px 14px rgba(0,0,0,0.7)' }}
            >
              {producer.name}
            </h4>
            <div className="flex items-center justify-between mt-2 text-[11px] text-white/70">
              {producer.city ? (
                <span className="inline-flex items-center gap-1 truncate">
                  <MapPin size={9} />
                  <span className="truncate">{producer.city}</span>
                </span>
              ) : <span />}
              {producer.upcoming_events_count > 0 && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/10"
                  style={{ color: accent }}
                >
                  {producer.upcoming_events_count} ev.
                </span>
              )}
            </div>
            {/* Flyer count chip (subtle, only when slideshow is active) */}
            {flyers.length > 1 && (
              <div className="flex items-center gap-1 mt-2.5">
                {flyers.map((_, i) => (
                  <span
                    key={i}
                    className={`h-[3px] rounded-full transition-all duration-500 ${
                      i === activeIdx ? 'w-5 bg-white/80' : 'w-2 bg-white/25'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
